import { useTradingSettings } from '@src/providers/AppConfigProvider';
import type { RfqBestQuoteBy } from '@src/utils/rfq';
import { ButtonGroup, ButtonVariants, HStack, Label, Stack, Text, ToggleButton, Tooltip } from '@talos/kyoko';
import type { ComponentProps } from 'react';

const OPTIONS = [
  { value: 'px', label: 'Px' },
  { value: 'qty', label: 'Qty' },
] satisfies { value: RfqBestQuoteBy; label: string }[];

const BEST_BY_TOOLTIP = (
  <Stack gap="spacing2">
    <Text>Controls how the RFQ best bid and offer are determined.</Text>
    <Text pl="spacing2">- Price (Px): selects the best price (highest bid, lowest offer)</Text>
    <Text pl="spacing2">- Quantity (Qty): selects the largest available size</Text>
    <Text>Ties are broken first by the other attribute (price vs. quantity), and then by timestamp.</Text>
  </Stack>
);

type RfqBestQuoteByToggleButtonProps = Omit<ComponentProps<typeof ToggleButton>, 'children' | 'onClick' | 'selected'>;

type RfqBestQuoteByToggleProps = ComponentProps<typeof ButtonGroup> & {
  toggleButtonProps?: RfqBestQuoteByToggleButtonProps;
};

export function RfqBestQuoteByToggle({ toggleButtonProps, ...buttonGroupProps }: RfqBestQuoteByToggleProps) {
  const { rfqBestQuoteBy, setRfqBestQuoteBy } = useTradingSettings();
  const {
    selectedVariant = ButtonVariants.Priority,
    unselectedVariant = ButtonVariants.Muted,
    ...restToggleButtonProps
  } = toggleButtonProps ?? {};

  return (
    <HStack
      gap="spacing6"
      alignItems="center"
      data-testid="rfq-best-quote-by-toggle"
      justifyContent="space-between"
      w="100%"
    >
      <Tooltip tooltip={BEST_BY_TOOLTIP}>
        <Label tooltip={true}>Best By</Label>
      </Tooltip>
      <ButtonGroup type="switch" {...buttonGroupProps}>
        {OPTIONS.map(option => (
          <ToggleButton
            {...restToggleButtonProps}
            key={option.value}
            selected={rfqBestQuoteBy === option.value}
            selectedVariant={selectedVariant}
            unselectedVariant={unselectedVariant}
            onClick={() => setRfqBestQuoteBy(option.value)}
            data-testid={`rfq-best-quote-by-${option.value}`}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ButtonGroup>
    </HStack>
  );
}
